import type { PerfTier, View } from '../types';

/** What each performance tier is allowed to draw. */
interface TierConfig {
  particles: number;
  connections: boolean;
  ripples: boolean;
  blobs: number;
  cursorGlow: boolean;
}

const TIER_CFG: Record<PerfTier, TierConfig> = {
  high: { particles: 140, connections: true, ripples: true, blobs: 3, cursorGlow: true },
  medium: { particles: 70, connections: false, ripples: true, blobs: 2, cursorGlow: true },
  low: { particles: 30, connections: false, ripples: false, blobs: 0, cursorGlow: false },
};

/** Per-page palette. The About page runs cooler and sparser than Home. */
interface PageConfig {
  particleMult: number;
  coreColor: string;
  glowColor: string;
  /** Prefix of an `rgba(...)` string — the alpha and closing paren are appended. */
  connColor: string;
  cursorGlow: string;
}

const PAGE_CFG: Record<View, PageConfig> = {
  home: {
    particleMult: 1.0,
    coreColor: 'rgba(255,155,80,0.9)',
    glowColor: 'rgba(200,130,70,0.65)',
    connColor: 'rgba(140,80,40,',
    cursorGlow: 'rgba(70,44,22,0.28)',
  },
  about: {
    particleMult: 0.5,
    coreColor: 'rgba(200,170,120,0.85)',
    glowColor: 'rgba(160,130,80,0.55)',
    connColor: 'rgba(100,70,40,',
    cursorGlow: 'rgba(55,45,35,0.25)',
  },
};

interface Blob {
  r: number;
  speed: number;
  phase: number;
  offset: number;
  color: string;
}

interface Particle {
  /** Grid anchor the particle orbits and springs back toward. */
  gx: number;
  gy: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  phase: number;
  speed: number;
  size: number;
}

interface Ripple {
  x: number;
  y: number;
  r: number;
  opacity: number;
}

/** Radius within which the cursor pushes particles away. */
const REPEL_RADIUS = 160;
/** Max distance at which two particles are joined by a line. */
const CONNECT_DIST = 130;
/**
 * Particles span the whole document, not just the viewport, so the canvas
 * needs the full scroll height. React content lands after mount, so height is
 * re-measured at these marks.
 */
const HEIGHT_RECALC_MS = [1200, 2500, 4000];

/** Handle returned by {@link initParticleCanvas}. */
export interface ParticleCanvas {
  /** Switch palettes when the view changes, and re-measure page height. */
  setView: (view: View) => void;
  /** Stop the animation and detach every listener. */
  destroy: () => void;
}

/**
 * Ambient particle background drawn behind the whole page.
 *
 * The canvas is `position: fixed`, so world-space particle coordinates are
 * offset by `scrollY` at draw time rather than the canvas being resized to the
 * document. Everything it draws is gated on the performance tier, and it
 * re-reads that tier live when the detector re-probes.
 */
export function initParticleCanvas(canvas: HTMLCanvasElement): ParticleCanvas {
  const context = canvas.getContext('2d');
  if (!context) {
    return { setView: () => {}, destroy: () => {} };
  }

  // Re-bound with an explicit type: `draw` is a hoisted function declaration,
  // so TypeScript will not carry the null-check narrowing into it.
  const ctx: CanvasRenderingContext2D = context;

  let width = 0;
  let height = 0;
  let pageHeight = 0;

  const resize = (): void => {
    width = canvas.width = window.innerWidth;
    height = canvas.height = window.innerHeight;
    pageHeight = measurePageHeight();
  };

  const measurePageHeight = (): number =>
    Math.max(
      document.documentElement.scrollHeight || height,
      document.body.scrollHeight || height,
      height * 4,
    );

  const particles: Particle[] = [];
  const ripples: Ripple[] = [];

  let view: View = 'home';
  let cfg: TierConfig = TIER_CFG[window.__PERF_TIER__ ?? 'high'];
  let particleCount = cfg.particles;

  let mouseX = 0;
  let mouseY = 0;
  let lastRippleX = -999;
  let lastRippleY = -999;
  let mouseActive = false;

  let scrollY = 0;
  let time = 0;
  let raf = 0;

  function initParticles(): void {
    particles.length = 0;

    const page = PAGE_CFG[view];
    const targetCount = Math.round(particleCount * page.particleMult);
    // Scale the grid so density stays even as the count changes.
    const densityScale = Math.sqrt(targetCount / 140);
    const cols = Math.max(4, Math.round(14 * densityScale));
    const rows = Math.max(4, Math.round(Math.max(10, pageHeight / 120) * densityScale));

    const startX = width * 0.02;
    const stepX = (width * 0.98) / cols;
    const stepY = pageHeight / rows;

    for (let r = 0; r < rows; r++) {
      const gy = r * stepY + stepY / 2;
      for (let c = 0; c < cols; c++) {
        const gx = startX + c * stepX + stepX / 2;
        particles.push({
          gx,
          gy,
          x: gx + (Math.random() - 0.5) * stepX * 0.6,
          y: gy + (Math.random() - 0.5) * stepY * 0.6,
          vx: 0,
          vy: 0,
          phase: Math.random() * Math.PI * 2,
          speed: 0.0003 + Math.random() * 0.0004,
          size: 2.2 + Math.random() * 2.2,
        });
      }
    }
  }

  const warmBlobs = (): readonly Blob[] => [
    { r: 600, speed: 0.0007, phase: 0, offset: 0, color: 'rgba(60,35,15,0.09)' },
    { r: 500, speed: 0.0005, phase: 2.1, offset: height * 0.2, color: 'rgba(50,30,12,0.07)' },
    { r: 700, speed: 0.0004, phase: 4.2, offset: -height * 0.1, color: 'rgba(55,32,14,0.06)' },
  ];

  const coolBlobs = (): readonly Blob[] => [
    { r: 600, speed: 0.0007, phase: 0, offset: 0, color: 'rgba(45,40,35,0.10)' },
    { r: 500, speed: 0.0005, phase: 2.1, offset: height * 0.2, color: 'rgba(40,36,32,0.08)' },
    { r: 700, speed: 0.0004, phase: 4.2, offset: -height * 0.1, color: 'rgba(42,38,34,0.07)' },
  ];

  function recalcHeight(): void {
    const next = measurePageHeight();
    // Only rebuild on a meaningful growth, not on every few-pixel reflow.
    if (next > pageHeight * 1.15) {
      pageHeight = next;
      initParticles();
    }
  }

  // ── Listeners ──────────────────────────────────────────────────

  const onScroll = (): void => {
    scrollY = window.scrollY;
  };

  const onResize = (): void => {
    resize();
    initParticles();
  };

  const onMouseMove = (event: MouseEvent): void => {
    mouseX = event.clientX;
    mouseY = event.clientY;
    mouseActive = true;

    const dx = mouseX - lastRippleX;
    const dy = mouseY - lastRippleY;
    if (dx * dx + dy * dy > 250) {
      ripples.push({ x: mouseX, y: mouseY, r: 0, opacity: 0.45 });
      lastRippleX = mouseX;
      lastRippleY = mouseY;
    }
  };

  const onTierChange = (): void => {
    cfg = TIER_CFG[window.__PERF_TIER__ ?? 'high'];
    if (cfg.particles !== particleCount) {
      particleCount = cfg.particles;
      initParticles();
    }
  };

  // ── Draw loop ──────────────────────────────────────────────────

  function draw(): void {
    time += 1;
    const page = PAGE_CFG[view];

    const base = ctx.createRadialGradient(width / 2, height * 0.35, 0, width / 2, height * 0.35, width);
    base.addColorStop(0, '#0c0c0c');
    base.addColorStop(1, '#050505');
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, width, height);

    // Ambient swirl blobs
    const blobs = (view === 'about' ? coolBlobs() : warmBlobs()).slice(0, cfg.blobs);
    for (const blob of blobs) {
      const angle = time * blob.speed + blob.phase;
      const bx = width / 2 + Math.cos(angle) * width * 0.35;
      const by = height / 2 + Math.sin(angle * 0.7) * height * 0.25 + blob.offset;

      const gradient = ctx.createRadialGradient(bx, by, 0, bx, by, blob.r);
      gradient.addColorStop(0, blob.color);
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }

    // Expanding ripples, newest last
    if (cfg.ripples) {
      for (let i = ripples.length - 1; i >= 0; i--) {
        const ripple = ripples[i];
        if (!ripple) continue;

        ripple.r += 2.6;
        ripple.opacity -= 0.002;
        if (ripple.opacity <= 0) {
          ripples.splice(i, 1);
          continue;
        }

        const gradient = ctx.createRadialGradient(ripple.x, ripple.y, 0, ripple.x, ripple.y, ripple.r);
        gradient.addColorStop(0, `rgba(75,48,26,${ripple.opacity})`);
        gradient.addColorStop(0.4, `rgba(50,30,16,${ripple.opacity * 0.6})`);
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
      }
    }

    // Flow-field particles
    const visible: { x: number; y: number }[] = [];

    for (const p of particles) {
      const angle = time * p.speed + p.phase;
      const orbitX = p.gx + Math.cos(angle) * 35;
      const orbitY = p.gy + Math.sin(angle * 0.7) * 28;

      if (mouseActive) {
        const dx = mouseX - p.x;
        const dy = mouseY - (p.y - scrollY);
        const distSq = dx * dx + dy * dy;
        const radiusSq = REPEL_RADIUS * REPEL_RADIUS;

        if (distSq < radiusSq && distSq > 1) {
          const force = (1 - distSq / radiusSq) * 1.2;
          const dist = Math.sqrt(distSq);
          p.vx -= (dx / dist) * force;
          p.vy -= (dy / dist) * force;
        }
      }

      // Spring back toward the orbit point, with damping.
      p.vx += (orbitX - p.x) * 0.008;
      p.vy += (orbitY - p.y) * 0.008;
      p.vx *= 0.96;
      p.vy *= 0.96;
      p.x += p.vx;
      p.y += p.vy;

      // The canvas is fixed, so shift world space into viewport space.
      const drawX = p.x;
      const drawY = p.y - scrollY;

      // Cull to the viewport plus half a screen of margin.
      if (drawY < -height * 0.5 || drawY > height * 1.5) continue;

      visible.push({ x: drawX, y: drawY });

      const glow = ctx.createRadialGradient(drawX, drawY, 0, drawX, drawY, p.size * 5);
      glow.addColorStop(0, page.glowColor);
      glow.addColorStop(1, 'transparent');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(drawX, drawY, p.size * 5, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = page.coreColor;
      ctx.beginPath();
      ctx.arc(drawX, drawY, p.size, 0, Math.PI * 2);
      ctx.fill();
    }

    // Faint links between nearby visible particles
    if (cfg.connections) {
      const maxDistSq = CONNECT_DIST * CONNECT_DIST;
      ctx.lineWidth = 0.6;

      for (let i = 0; i < visible.length; i++) {
        const a = visible[i];
        if (!a) continue;

        for (let j = i + 1; j < visible.length; j++) {
          const b = visible[j];
          if (!b) continue;

          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const distSq = dx * dx + dy * dy;
          if (distSq >= maxDistSq) continue;

          ctx.strokeStyle = `${page.connColor}${(1 - distSq / maxDistSq) * 0.22})`;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
    }

    // Cursor glow
    if (cfg.cursorGlow) {
      const gradient = ctx.createRadialGradient(mouseX, mouseY, 0, mouseX, mouseY, 180);
      gradient.addColorStop(0, page.cursorGlow);
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }

    raf = requestAnimationFrame(draw);
  }

  // ── Boot ───────────────────────────────────────────────────────

  resize();
  initParticles();
  mouseX = width / 2;
  mouseY = height / 2;

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  document.addEventListener('mousemove', onMouseMove);
  window.addEventListener('perftierready', onTierChange);

  const timers = HEIGHT_RECALC_MS.map((ms) => window.setTimeout(recalcHeight, ms));

  raf = requestAnimationFrame(draw);

  return {
    setView(next: View) {
      view = next;
      initParticles();
      recalcHeight();
    },
    destroy() {
      cancelAnimationFrame(raf);
      timers.forEach(window.clearTimeout);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('perftierready', onTierChange);
    },
  };
}
